import * as React from 'react'

import { Box, Button, Text } from '@factorialco/deprecated-design-system'
import { ProcessTargetDataTableDocument } from 'generated/resources/datatable.graphql'
import {
  DeleteTargetDocument,
  PerformanceReviewOverviewHeaderDocument,
  PerformanceTargetAvatarFragment,
  PerformanceTargetManagementDocument,
} from 'generated/resources/graphql'

import Modal from 'modules/core/components/Modal'
import { ClosePortalFn } from 'modules/core/components/Portal/useCloseProxy'
import TextField from 'modules/core/components/TextField'
import useAsyncAction from 'modules/core/hooks/useAsyncAction'
import { useMutation } from 'modules/core/hooks/useMutation'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'

export const DeleteTargetModal: React.FC<{
  processTargetId: string
  targetAccess: PerformanceTargetAvatarFragment
  onClose: () => void
}> = ({ onClose, targetAccess, processTargetId }) => {
  const name = targetAccess.employeeName?.fullName
  const [inputName, setInputName] = React.useState<string>('')
  const inputIsOk = inputName.trim() === name?.trim()
  const [deleteTarget, { loading }] = useMutation(DeleteTargetDocument, {
    extractorFn: (data) => data.performanceMutations.deletePerformanceReviewProcessTarget,
  })

  const { running, performAction: onSubmit } = useAsyncAction(
    async (closePortal: ClosePortalFn) => {
      const response = await deleteTarget({
        awaitRefetchQueries: true,
        variables: {
          id: processTargetId,
        },
        refetchQueries: [
          ProcessTargetDataTableDocument,
          PerformanceTargetManagementDocument,
          PerformanceReviewOverviewHeaderDocument,
        ],
      })

      const handled = handleResult(response, () => undefined)

      closePortal()
      return handled
    }
  )

  return (
    <Modal
      title={i18n.t('performance_process.reviews.target_management_deletion_title')}
      description={i18n.t('performance_process.reviews.target_management_deletion_description')}
      illustration="alert"
      onClose={onClose}
      onSubmit={onSubmit}
      footer={
        <>
          <Button type="secondary" onClick={onClose}>
            {i18n.t('common.cancel')}
          </Button>
          <Button type="primary" disabled={running || loading || !inputIsOk} submit>
            {i18n.t('common.delete')}
          </Button>
        </>
      }
    >
      <Box flexDirection="column" gap="s8">
        <Text.Normal whiteSpace="breakSpaces">
          {i18n.t('performance_process.reviews.target_management_deletion_input', { name })}
        </Text.Normal>
        <TextField onChange={setInputName} value={inputName} />
      </Box>
    </Modal>
  )
}
